import * as React from 'react';
import { toast as sonnerToast } from 'sonner';

/** Default auto-dismiss durations (ms); errors and warnings linger longer. */
const DEFAULT_DURATION = 3500;
const ATTENTION_DURATION = 5000;

type ToastVariant = 'default' | 'destructive' | 'success' | 'warning' | 'info';

interface ToastInput {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  duration?: number;
  id?: string | number;
  /** Inline action button (sonner-native); use for "take me there" affordances. */
  action?: { label: React.ReactNode; onClick: () => void };
}

type ToastShorthandOptions = Omit<ToastInput, 'title' | 'variant'>;

function wrap(id: string | number) {
  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: (props: ToastInput) => legacyToast({ ...props, id }),
  };
}

/** Object form kept for backward compatibility: toast({ title, description, variant }). */
function legacyToast({ title, variant, ...options }: ToastInput) {
  switch (variant) {
    case 'destructive':
      return toastHelpers.error(title, options);
    case 'success':
      return toastHelpers.success(title, options);
    case 'warning':
      return toastHelpers.warning(title, options);
    case 'info':
      return toastHelpers.info(title, options);
    default:
      return wrap(sonnerToast(title, { duration: DEFAULT_DURATION, ...options }));
  }
}

const toastHelpers = {
  success: (title: React.ReactNode, options?: ToastShorthandOptions) =>
    wrap(sonnerToast.success(title, { duration: DEFAULT_DURATION, ...options })),
  error: (title: React.ReactNode, options?: ToastShorthandOptions) =>
    wrap(sonnerToast.error(title, { duration: ATTENTION_DURATION, ...options })),
  warning: (title: React.ReactNode, options?: ToastShorthandOptions) =>
    wrap(sonnerToast.warning(title, { duration: ATTENTION_DURATION, ...options })),
  info: (title: React.ReactNode, options?: ToastShorthandOptions) =>
    wrap(sonnerToast.info(title, { duration: DEFAULT_DURATION, ...options })),
};

/** Prefer the state helpers so every toast carries a state colour. The object form stays supported. */
const toast = Object.assign(legacyToast, {
  ...toastHelpers,
  message: sonnerToast.message,
  loading: sonnerToast.loading,
  promise: sonnerToast.promise,
  custom: sonnerToast.custom,
  dismiss: sonnerToast.dismiss,
});

function useToast() {
  return {
    toast,
    dismiss: (id?: string | number) => sonnerToast.dismiss(id),
  };
}

export { useToast, toast };
